import { useEffect, useState, MutableRefObject } from 'react'

import useIsServer from 'hooks/useIsServer'

type HlsInstance = {
  loadSource: (src: string) => void
  attachMedia: (el: HTMLVideoElement) => void
  startLoad: () => void
  destroy: () => void
}

// hls.js (~150KB) loads only on non-native-HLS browsers. autoStartLoad is off
// so video segments buffer on first play (startLoad), not at render.
const loadVideo = async (
  video: HTMLVideoElement,
  videoSrc: string,
): Promise<HlsInstance | undefined> => {
  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // eslint-disable-next-line no-param-reassign
    video.src = videoSrc
    return undefined
  }

  const { default: Hls } = await import(/* webpackChunkName: "hls" */ 'hls.js')

  if (!Hls?.isSupported()) {
    // eslint-disable-next-line no-console
    console.error("This is a legacy browser that doesn't support MSE")
    return undefined
  }

  const hls = new Hls({
    enableWorker: true,
    lowLatencyMode: true,
    capLevelToPlayerSize: true,
    backBufferLength: 90,
    autoStartLoad: false,
  })
  hls.loadSource(videoSrc)
  hls.attachMedia(video)
  return hls
}

export const useVideoPlaying = (
  videoRef: MutableRefObject<HTMLVideoElement>,
) => {
  const [videoPlaying, setVideoPlaying] = useState(false)

  useEffect(() => {
    videoRef.current?.addEventListener('play', () => setVideoPlaying(true))
    videoRef.current?.addEventListener('pause', () => setVideoPlaying(false))

    return () => {
      videoRef.current?.removeEventListener('play', () => setVideoPlaying(true))
      videoRef.current?.removeEventListener('pause', () =>
        setVideoPlaying(false),
      )
    }
  }, [videoRef])

  return videoPlaying
}

export const useForcePause = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  forcePause: boolean,
) => {
  useEffect(() => {
    if (videoRef.current != null && forcePause === true) {
      videoRef.current.pause()
    }
  }, [forcePause, videoRef])
}

export const useLoadVideo = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  src: string,
) => {
  const isServer = useIsServer()
  useEffect(() => {
    // loadVideo is async because hls.js loads on demand. Track the resolved
    // instance and a `cancelled` flag so cleanup destroys the right hls even
    // if the effect re-runs before the promise settles. autoStartLoad is off,
    // so segments only buffer once the user plays (startLoad) — wired here, not
    // in loadVideo, so the listener is removed on cleanup if src changes.
    const video = videoRef.current
    let hls: HlsInstance | undefined
    let cancelled = false
    const startLoad = () => hls?.startLoad()
    if (video && src) {
      loadVideo(video, src).then((instance) => {
        if (cancelled) {
          instance?.destroy()
        } else {
          hls = instance
          if (hls) {
            video.addEventListener('play', startLoad, { once: true })
          }
        }
      })
    }

    return () => {
      cancelled = true
      video?.removeEventListener('play', startLoad)
      if (hls) {
        hls.destroy()
      }
    }
  }, [videoRef, src, isServer])
}
